import { useEffect, useRef } from 'react';
import { StatusToastContainerWrapper, StatusToastItemWrapper } from './StatusToast.style';
import { StatusToast, StatusToastHandle } from './StatusToast';
import { ReplyToast, ReplyToastHandle } from './ReplyToast';
import { StatusToastOverlay } from './StatusToastOverlay';
import { useStatusToastContext } from './StatusToastContext';

export const StatusToastContainer = () => {
	const { toasts, removeToast, overlay, closeOverlay } = useStatusToastContext();
	const containerRef = useRef<HTMLDivElement>(null);
	const toastRefs = useRef<Map<string, StatusToastHandle | ReplyToastHandle>>(new Map());

	const makeRefCallback = (id: string) => (handle: StatusToastHandle | ReplyToastHandle | null) => {
		if (handle) {
			toastRefs.current.set(id, handle);
		} else {
			toastRefs.current.delete(id);
		}
	};

	// Smooth height transitions when items are added/removed
	useEffect(() => {
		if (containerRef.current) {
			const container = containerRef.current;
			// Force reflow to ensure smooth transition
			void container.offsetHeight;
		}
	}, [toasts.length]);

	// Handle overlay closing - trigger toast exit animation
	// Note: This is called by the overlay after its exit animation completes
	const handleOverlayClose = () => {
		if (overlay.toastId) {
			const toastHandle = toastRefs.current.get(overlay.toastId);
			if (toastHandle) {
				toastHandle.hide();
			}
		}
		// Clear the overlay state (this happens after animation completes)
		closeOverlay();
	};

	return (
		<>
			{toasts.length > 0 && (
				<StatusToastContainerWrapper ref={containerRef}>
					{toasts.map((toast) => (
						<StatusToastItemWrapper key={toast.id}>
							{toast.variant === 'reply' ? (
								<ReplyToast
									toast={toast}
									onHide={() => {
										toastRefs.current.delete(toast.id);
										removeToast(toast.id);
									}}
									ref={makeRefCallback(toast.id)}
								/>
							) : (
								<StatusToast
									toast={toast}
									onHide={() => {
										toastRefs.current.delete(toast.id);
										removeToast(toast.id);
									}}
									ref={makeRefCallback(toast.id)}
								/>
							)}
						</StatusToastItemWrapper>
					))}
				</StatusToastContainerWrapper>
			)}
			<StatusToastOverlay
				isOpen={overlay.isOpen}
				onClose={handleOverlayClose}
				title={overlay.title || ''}
				mainText={overlay.mainText || ''}
				items={overlay.items}
			/>
		</>
	);
};

